import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { IoArrowBackSharp } from "react-icons/io5";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import Select from "react-select";
import { Button, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import swal from "sweetalert";
import {
  customOption,
  customSingleValue,
  decodeHtmlEntities,
  LoadingSpinner,
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles } from "../../helper/Method";
import { ApiFinance } from "../ApiFinance";
import { getMinMaxDates, validateTransactionDate } from "../fiscalYearHelper";

const AssetDonation = ({ history }) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));

  const [loading, setLoading] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [assetAccounts, setAssetAccounts] = useState([]);
  const [assetDimensions, setAssetDimensions] = useState([]);

  const [formData, setFormData] = useState({
    assetCategory: null,
    asset: null,
    estimatedValue: "",
    date: new Date(),
    memo: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  useEffect(() => {
    if (!loggedInUnitId) return;
    setLookupsLoading(true);
    ApiFinance()
      .GetAssetDonationLookups(loggedInUnitId)
      .then((res) => {
        const accounts = res.data?.assetAccounts || res.data?.AssetAccounts || [];
        const dimensions =
          res.data?.assetDimensions || res.data?.AssetDimensions || [];
        const decodeOption = (item) => ({
          ...item,
          label: decodeHtmlEntities(item.label || item.Label || ""),
        });
        setAssetAccounts(accounts.map(decodeOption));
        setAssetDimensions(dimensions.map(decodeOption));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text:
            err?.response?.data?.message ||
            err?.response?.data ||
            "Unable to load asset donation lookups.",
          icon: "error",
          buttons: "OK",
        });
        setAssetAccounts([]);
        setAssetDimensions([]);
      })
      .finally(() => setLookupsLoading(false));
  }, [loggedInUnitId]);

  const validate = () => {
    const temp = {};
    temp.assetCategory = formData.assetCategory ? (
      ""
    ) : (
      <GetLanguageString props="common_field_required_error" />
    );
    temp.asset = formData.asset ? (
      ""
    ) : (
      <GetLanguageString props="common_field_required_error" />
    );

    const amount = parseFloat(formData.estimatedValue);
    if (
      formData.estimatedValue === "" ||
      formData.estimatedValue === null ||
      Number.isNaN(amount)
    ) {
      temp.estimatedValue = (
        <GetLanguageString props="funds_add_fund_amount_paid_error" />
      );
    } else if (amount <= 0) {
      temp.estimatedValue = t("finance_asset_donation_amount_must_be_positive");
    } else if (
      !/^\d+(\.\d{1,2})?$/.test(String(formData.estimatedValue).trim())
    ) {
      temp.estimatedValue = t("finance_asset_donation_amount_decimal_error");
    } else {
      temp.estimatedValue = "";
    }

    temp.memo =
      formData.memo && formData.memo.trim() !== "" ? (
        ""
      ) : (
        <GetLanguageString props="common_field_required_error" />
      );

    const dateError = validateTransactionDate(formData.date);
    if (dateError) {
      temp.date = t(dateError);
    } else {
      temp.date = "";
    }

    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validate()) return;

    const payload = {
      financeAccount: formData.assetCategory,
      dimension2: formData.asset,
      date: formData.date,
      amount: parseFloat(formData.estimatedValue),
      memo: formData.memo.trim(),
      unitId: loggedInUnitId,
    };

    setLoading(true);
    ApiFinance()
      .AddAssetDonation(payload)
      .then((res) => {
        const data = res.data;
        if (Number(data?.statusCode) === 200) {
          swal({
            title: "Success",
            text: t(data.message) || t("finance_asset_donation_added_successfully"),
            icon: "success",
            buttons: "OK",
          }).then(() => history.goBack());
          return;
        }

        swal({
          title: "Error",
          text: data?.message || t("finance_asset_donation_failed"),
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          err?.response?.data ||
          t("finance_asset_donation_failed");
        swal({
          title: "Error",
          text: typeof message === "string" ? message : t("finance_asset_donation_failed"),
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
      });
  };

  const { minDate, maxDate } = getMinMaxDates(formData.date);

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div className="ibox-title d-flex justify-content-between align-items-center">
          <h5>
            {<GetLanguageString props="funds_asset_donation" />}
          </h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>

        <div className="ibox-content">
          {loading || lookupsLoading ? (
            LoadingSpinner()
          ) : (
            <Form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="finance_asset_category" />}
                    </Label>
                    <Select
                      components={{
                        Option: customOption,
                        SingleValue: customSingleValue,
                      }}
                      options={assetAccounts}
                      value={formData.assetCategory}
                      onChange={(selected) =>
                        handleChange("assetCategory", selected)
                      }
                      classNamePrefix="select"
                      placeholder=""
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          borderColor: errors.assetCategory
                            ? "#dc3545"
                            : state.isFocused
                              ? "#1ab394"
                              : "#e5e6e7",
                        }),
                      }}
                    />
                    {errors.assetCategory && (
                      <h4 className="text-error no-drag">
                        {errors.assetCategory}
                      </h4>
                    )}
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="finance_asset" />}
                    </Label>
                    <Select
                      components={{
                        Option: customOption,
                        SingleValue: customSingleValue,
                      }}
                      options={assetDimensions}
                      value={formData.asset}
                      onChange={(selected) => handleChange("asset", selected)}
                      classNamePrefix="select"
                      placeholder=""
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          borderColor: errors.asset
                            ? "#dc3545"
                            : state.isFocused
                              ? "#1ab394"
                              : "#e5e6e7",
                        }),
                      }}
                    />
                    {errors.asset && (
                      <h4 className="text-error no-drag">{errors.asset}</h4>
                    )}
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="finance_estimated_value" />}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.estimatedValue}
                      onChange={(e) =>
                        handleChange("estimatedValue", e.target.value)
                      }
                      className="no-drag"
                      invalid={!!errors.estimatedValue}
                    />
                    {errors.estimatedValue && (
                      <h4 className="text-error no-drag">
                        {errors.estimatedValue}
                      </h4>
                    )}
                  </FormGroup>
                </Col>
                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="funds_date" />}
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      className="form-control no-drag"
                      selected={formData.date}
                      onChange={(date) => handleChange("date", date)}
                      dateFormat="dd/MM/yyyy"
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                    {errors.date && (
                      <h4 className="text-error no-drag">{errors.date}</h4>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="col-form-label">
                      {<GetLanguageString props="transactions_memo" />}
                    </Label>
                    <Input
                      type="textarea"
                      value={formData.memo}
                      onChange={(e) => handleChange("memo", e.target.value)}
                      className="no-drag"
                      invalid={!!errors.memo}
                    />
                    {errors.memo && (
                      <h4 className="text-error no-drag">{errors.memo}</h4>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12" className="text-center">
                  <Button color="primary" type="submit" disabled={loading}>
                    {<GetLanguageString props="common_save" />}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(AssetDonation);
